import { FoodLibraryMeal } from "@/clients/models/FoodLibraryMeal";
import { ChevronDownIcon } from "@/components/icons/chevron-down-icon";
import { CloseIcon } from "@/components/icons/close-icon";
import { SearchIcon } from "@/components/icons/search-icon";
import IconButton from "@/components/ui/buttons/IconButton";
import { TextButton } from "@/components/ui/buttons/TextButton";
import { FoodLibraryList } from "@/features/meal-plan/components/food-library-list";
import {
  SAVED_MEALS_TAB_OPTIONS,
  SavedMealsTabValue,
} from "@/features/meal-plan/constants/saved-meals-tabs";
import { cn } from "@/utils/cn";
import { IonContent, IonModal } from "@ionic/react";
import { memo, useState } from "react";
import { MealDetailModal } from "./meal-detail-modal";

interface FavouriteFoodsSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAddMeal?: (meal: FoodLibraryMeal) => void;
  isAddingDisabled?: boolean;
}

function SortIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 6h13M3 12h9M3 18h5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M19 6v12m0 0-2.5-2.5M19 18l2.5-2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const FavouriteFoodsSheet = memo(function FavouriteFoodsSheet({
  isOpen,
  onDismiss,
  onAddMeal,
  isAddingDisabled = false,
}: FavouriteFoodsSheetProps) {
  const [selectedTab, setSelectedTab] = useState<SavedMealsTabValue>("your-food");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMeal, setSelectedMeal] = useState<FoodLibraryMeal | null>(null);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonContent>
        <div className="relative flex h-full flex-col">
          {/* Header + Tabs — lavender background section */}
          <div className="safe-area-top bg-[#f4f2fd]">
            {/* Header */}
            <div className="flex items-center px-2 pt-3 pb-[9px]">
              <IconButton
                aria-label="Close"
                onClick={onDismiss}
                className="bg-transparent hover:bg-black/5 active:bg-black/5"
              >
                <ChevronDownIcon />
              </IconButton>
              <div className="flex flex-1 items-center justify-center">
                <span className="text-[18px] font-bold">Foods Added</span>
              </div>
              <IconButton
                aria-label="Sort"
                className="text-os-brand bg-transparent opacity-0 hover:bg-black/5 active:bg-black/5"
              >
                <SortIcon />
              </IconButton>
            </div>

            {/* Tabs */}
            <div className="flex items-center px-4">
              {SAVED_MEALS_TAB_OPTIONS.map((tab) => {
                const isActive = selectedTab === tab.value;
                return (
                  <button
                    key={tab.value}
                    onClick={() => {
                      setSelectedTab(tab.value as SavedMealsTabValue);
                      setSearchQuery("");
                    }}
                    className="flex h-11 flex-1 flex-col items-center justify-end"
                  >
                    <div className="relative flex flex-col items-center pt-2 pb-1">
                      <span
                        className={cn(
                          "mb-[14px] text-sm font-bold",
                          isActive ? "text-os-brand" : "text-gray-500",
                        )}
                      >
                        {tab.label}
                      </span>
                      <div
                        className={cn(
                          "absolute -right-3 bottom-0 -left-3 h-1 rounded",
                          isActive ? "bg-os-brand" : "bg-transparent",
                        )}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search bar */}
          <div className="mt-[22px] px-4">
            <div className="focus-within:ring-os-brand flex h-11 items-center gap-2 rounded-[14px] bg-[#f4f2fd] pr-2 pl-3 ring-1 ring-transparent transition-shadow">
              <SearchIcon className="h-5 w-5 shrink-0 fill-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search foods, meals or brands"
                className="flex-1 bg-transparent text-sm font-medium text-gray-950 outline-none placeholder:text-gray-500 focus:placeholder:opacity-60"
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery("")}
                  className="bg-os-brand flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                >
                  <CloseIcon className="h-3 w-3 fill-white text-white" />
                </button>
              )}
            </div>
          </div>

          <div className="no-scrollbar flex-1 overflow-y-auto px-4 pt-4 pb-6">
            <FoodLibraryList
              tab={selectedTab}
              searchQuery={searchQuery}
              onClearSearch={() => setSearchQuery("")}
              variant="flat"
              onMealClick={setSelectedMeal}
              renderMealAction={
                onAddMeal
                  ? (meal) => {
                      if (!meal.id) return null;
                      return (
                        <TextButton
                          type="button"
                          aria-label={`Add ${meal.name}`}
                          disabled={isAddingDisabled}
                          onClick={() => onAddMeal(meal)}
                          className="text-os-brand h-10 w-auto shrink-0 px-2 text-[14px]"
                        >
                          Add
                        </TextButton>
                      );
                    }
                  : undefined
              }
            />
          </div>

          <MealDetailModal
            meal={selectedMeal}
            isOpen={selectedMeal !== null}
            onDismiss={() => setSelectedMeal(null)}
            onMealUpdated={setSelectedMeal}
          />
        </div>
      </IonContent>
    </IonModal>
  );
});
